import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@artosera/ui';
import {
  useComptesBancaires,
  useApercuBancaire,
  useImporterReleve,
  messageTresorerie,
  type PresetCsv,
} from '../../api/tresorerie.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { TresorerieCadre, Montant, DateJour, EtatRequete } from './commun.js';
import styles from './Tresorerie.module.css';
function Contenu() {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const comptes = useComptesBancaires(galerieSlug);
  const apercu = useApercuBancaire(galerieSlug);
  const importer = useImporterReleve(galerieSlug);
  const [compteId, setCompte] = useState('');
  const [fichier, setFichier] = useState<File>();
  const [csv, setCsv] = useState(false);
  const [separe, setSepare] = useState(false);
  const [colonnes, setColonnes] = useState<Record<string, string | number>>({
    dateOperation: 'Date',
    libelle: 'Libellé',
    montant: 'Montant',
    debit: 'Débit',
    credit: 'Crédit',
  });
  const [decimal, setDecimal] = useState<PresetCsv['decimal']>(',');
  const [formatDate, setFormatDate] = useState<PresetCsv['formatDate']>('JJ/MM/AAAA');
  const [separateur, setSeparateur] = useState('');
  const [ignorer, setIgnorer] = useState(0);
  const [erreur, setErreur] = useState('');
  const label = (c: string) => t(`tresorerie.${c}`);
  const peutEcrire = galerieALaction(galerie, 'treasury:write');
  function changer() {
    apercu.reset();
    importer.reset();
    setErreur('');
  }
  function preset(): PresetCsv | undefined {
    if (!csv) return undefined;
    const c = { dateOperation: colonnes['dateOperation']!, libelle: colonnes['libelle']! };
    return {
      colonnes: {
        ...c,
        ...(separe
          ? { debit: colonnes['debit']!, credit: colonnes['credit']! }
          : { montant: colonnes['montant']! }),
        ...(colonnes['dateValeur'] !== undefined && colonnes['dateValeur'] !== ''
          ? { dateValeur: colonnes['dateValeur'] }
          : {}),
        ...(colonnes['reference'] !== undefined && colonnes['reference'] !== ''
          ? { reference: colonnes['reference'] }
          : {}),
      },
      decimal,
      formatDate,
      ignorerLignes: ignorer,
      ...(separateur ? { separateur: separateur as PresetCsv['separateur'] } : {}),
    };
  }
  const occupe = apercu.isPending || importer.isPending;
  async function envoyer(previsualiser: boolean) {
    if (!fichier || !compteId || !peutEcrire) return;
    setErreur('');
    const v = { compteId, fichier, presetCsv: preset() };
    try {
      if (previsualiser) await apercu.mutateAsync(v);
      else await importer.mutateAsync(v);
    } catch (e) {
      setErreur(messageTresorerie(e));
    }
  }
  if (!peutEcrire) return null;
  return (
    <>
      <EtatRequete requete={comptes} />
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void envoyer(true);
        }}
      >
        <fieldset disabled={occupe}>
          <legend>{label('fichier')}</legend>
          <Field htmlFor="import-compte" label={label('compte')}>
            <Select
              id="import-compte"
              value={compteId}
              onValueChange={(v) => {
                setCompte(v);
                changer();
                const p = comptes.data?.comptes.find((c) => c.id === v)?.presetCsv;
                if (p) {
                  setColonnes(p.colonnes);
                  setSepare(p.colonnes.montant === undefined);
                  setDecimal(p.decimal);
                  setFormatDate(p.formatDate);
                  setIgnorer(p.ignorerLignes ?? 0);
                  setSeparateur(p.separateur ?? '');
                }
              }}
              options={(comptes.data?.comptes ?? [])
                .filter((c) => !c.ferme)
                .map((c) => ({ value: c.id, label: `${c.libelle} · ${c.iban}` }))}
            />
          </Field>
          <Field htmlFor="import-fichier" label={label('fichier')}>
            <Input
              id="import-fichier"
              type="file"
              accept=".csv,.ofx,.xml,.txt"
              required
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFichier(f);
                setCsv(!!f?.name.toLowerCase().endsWith('.csv'));
                changer();
              }}
            />
          </Field>
          <Field htmlFor="import-format" label={label('format')}>
            <Select
              id="import-format"
              value={csv ? 'csv' : 'auto'}
              onValueChange={(v) => {
                setCsv(v === 'csv');
                changer();
              }}
              options={[
                { value: 'auto', label: 'CAMT.053 / OFX' },
                { value: 'csv', label: 'CSV' },
              ]}
            />
          </Field>
          {csv && (
            <>
              <p>{label('colonnesAide')}</p>
              <Field htmlFor="import-montants" label={label('colonnesMontants')}>
                <Select
                  id="import-montants"
                  value={separe ? 'separe' : 'montant'}
                  onValueChange={(v) => {
                    setSepare(v === 'separe');
                    changer();
                  }}
                  options={[
                    { value: 'montant', label: label('montantSigne') },
                    { value: 'separe', label: label('debitCredit') },
                  ]}
                />
              </Field>
              {[
                'dateOperation',
                'libelle',
                ...(separe ? ['debit', 'credit'] : ['montant']),
                'dateValeur',
                'reference',
              ].map((c) => (
                <Field key={c} htmlFor={`colonne-${c}`} label={label(`colonnes.${c}`)}>
                  <Input
                    id={`colonne-${c}`}
                    value={colonnes[c] ?? ''}
                    required={!['dateValeur', 'reference'].includes(c)}
                    onChange={(e) => {
                      setColonnes({ ...colonnes, [c]: e.target.value });
                      changer();
                    }}
                  />
                </Field>
              ))}
              <Field htmlFor="import-decimal" label={label('decimal')}>
                <Select
                  id="import-decimal"
                  value={decimal}
                  onValueChange={(v) => {
                    setDecimal(v as PresetCsv['decimal']);
                    changer();
                  }}
                  options={[
                    { value: ',', label: ',' },
                    { value: '.', label: '.' },
                  ]}
                />
              </Field>
              <Field htmlFor="import-date" label={label('formatDate')}>
                <Select
                  id="import-date"
                  value={formatDate}
                  onValueChange={(v) => {
                    setFormatDate(v as PresetCsv['formatDate']);
                    changer();
                  }}
                  options={['JJ/MM/AAAA', 'AAAA-MM-JJ', 'JJ-MM-AAAA'].map((v) => ({
                    value: v,
                    label: v,
                  }))}
                />
              </Field>
              <Field htmlFor="import-separateur" label={label('separateur')}>
                <select
                  id="import-separateur"
                  value={separateur}
                  onChange={(e) => {
                    setSeparateur(e.target.value);
                    changer();
                  }}
                >
                  {[
                    ['', label('automatique')],
                    [';', ';'],
                    [',', ','],
                    ['\t', label('tabulation')],
                    ['|', '|'],
                  ].map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field htmlFor="import-ignorer" label={label('ignorerLignes')}>
                <Input
                  id="import-ignorer"
                  type="number"
                  min={0}
                  max={20}
                  value={ignorer}
                  onChange={(e) => {
                    setIgnorer(Number(e.target.value));
                    changer();
                  }}
                />
              </Field>
            </>
          )}
          <Button type="submit" disabled={!compteId || !fichier} loading={apercu.isPending}>
            {label('apercu')}
          </Button>
        </fieldset>
        {erreur && <p role="alert">{erreur}</p>}
        {apercu.data && (
          <>
            <p>
              {label('lues')} : {apercu.data.lues}
            </p>
            <div className={styles.tableau}>
              <table>
                <caption>{label('apercu')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{label('date')}</th>
                    <th scope="col">{label('libelle')}</th>
                    <th scope="col">{label('montant')}</th>
                  </tr>
                </thead>
                <tbody>
                  {apercu.data.lignes.map((l, n) => (
                    <tr key={n}>
                      <td>
                        <DateJour date={l.dateOperation} />
                      </td>
                      <td>{l.libelle}</td>
                      <td>
                        <Montant cents={l.montantCentimes} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              type="button"
              loading={importer.isPending}
              disabled={occupe || !!importer.data}
              onClick={() => void envoyer(false)}
            >
              {label('confirmerImport')}
            </Button>
          </>
        )}
        {importer.data && (
          <p role="status">
            {label('lues')} : {importer.data.nombres.lues} · {label('nouvelles')} :{' '}
            {importer.data.nombres.nouvelles} · {label('connues')} : {importer.data.nombres.connues}{' '}
            · {label('rapprochementEnAttente')}
          </p>
        )}
      </form>
    </>
  );
}
export function ImportPage() {
  return (
    <TresorerieCadre titre="importer">
      <Contenu />
    </TresorerieCadre>
  );
}
